/**
 * The web console's cross-page marks list (decisions and open questions),
 * read from the team's marks index over the project's TeamRoom socket. The
 * mapping and filtering are the desktop's own (`@nimbalyst/collab-bundle/pages`).
 *
 * Updated servers include live typed-page bodies. Older-server responses stay
 * visibly partial. Open lists load again when the TeamRoom says marks changed
 * or the socket reopens, and keep retrying while the answer is missing or
 * partial.
 */

import type { PageMarkEntryKind } from '@nimbalyst/collab-protocol';
import {
  filterPageMarks,
  onTeamPageMarksChanged,
  PageMarksChangeFeed,
  pageMarkRecordsFromTeamIndex,
  type PageMarksSource,
  type TeamPageMarksResult,
} from '@nimbalyst/collab-bundle/pages';

const typedPageListeners = new Set<() => void>();
/** Tracker mutations can archive/delete a page without changing its body. */
export function notifyWebTypedPageMarksChanged(): void {
  for (const listener of typedPageListeners) listener();
}

export interface WebPageMarksSourceOptions {
  orgId: string;
  /** Null when the TeamRoom is not connected or did not answer. */
  query(filters: { kind?: PageMarkEntryKind; email?: string }): Promise<TeamPageMarksResult | null>;
}

export function createWebPageMarksSource(options: WebPageMarksSourceOptions): PageMarksSource {
  const feed = new PageMarksChangeFeed();
  const read: NonNullable<PageMarksSource['listMarksResult']> = async (query) => {
    const result = await options.query({
      ...(query.kind ? { kind: query.kind } : {}),
      ...(query.email ? { email: query.email } : {}),
    });
    feed.settled(result?.status === 'ready');
    if (!result) throw new Error('Marks are unavailable until the team connection is back');
    return { marks: filterPageMarks(pageMarkRecordsFromTeamIndex(result.marks, { orgId: options.orgId }), query),
      status: result.status === 'ready' && result.coverage === 'all-page-kinds' ? 'ready' : 'partial' };
  };
  return {
    listMarks: async query => (await read(query)).marks,
    listMarksResult: read,
    subscribe(listener) {
      const stopRetries = feed.subscribe(listener);
      const stopTeam = onTeamPageMarksChanged(listener);
      typedPageListeners.add(listener);
      return () => {
        stopRetries();
        stopTeam();
        typedPageListeners.delete(listener);
      };
    },
  };
}
